import { useState, useRef, useEffect } from 'react';

// Chip-style pill selector. See .ze-chip in explorer.css for styling.
export function FilterPills<T extends string>({
  value,
  options,
  labels,
  onChange,
  activeClass = 'ze-chip--active',
}: {
  value: T;
  options: readonly T[];
  labels?: Partial<Record<T, string>>;
  onChange: (v: T) => void;
  activeClass?: string;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {options.map((opt) => (
        <button
          key={opt}
          className={`ze-chip${value === opt ? ` ${activeClass}` : ''}`}
          onClick={() => onChange(opt)}
        >
          {labels?.[opt] ?? opt}
        </button>
      ))}
    </div>
  );
}

// Multi-toggle version.
export function MultiFilterPills({
  values,
  labels,
  onChange,
  activeClass = 'ze-chip--active',
}: {
  values: Record<string, boolean>;
  labels?: Record<string, string>;
  onChange: (key: string) => void;
  activeClass?: string;
}) {
  const keys = Object.keys(values);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {keys.map((k) => (
        <button
          key={k}
          className={`ze-chip${values[k] ? ` ${activeClass}` : ''}`}
          onClick={() => onChange(k)}
        >
          {labels?.[k] ?? k}
        </button>
      ))}
    </div>
  );
}

/**
 * Variable/band selector. Label sits on its own line above the chip group,
 * so the chips wrap freely beneath — the label doesn't repeat per row.
 */
export function BandSelector<T extends string>({
  value,
  options,
  onChange,
  label = 'Variable',
  labels,
}: {
  value: T;
  options: readonly T[];
  onChange: (v: T) => void;
  label?: string;
  labels?: Partial<Record<T, string>>;
}) {
  return (
    <div className="ze-control-row">
      <div className="ze-section-label">{label}</div>
      <FilterPills value={value} options={options} onChange={onChange} labels={labels} />
    </div>
  );
}

// Slider — used by the time controls only now that the sidebar time sliders
// were removed. Kept stacked-friendly.
export function Slider({
  value,
  onChange,
  min = 0,
  max = 10,
  step = 1,
  label = 'Value',
  formatValue,
  parseValue,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  formatValue?: (v: number) => string;
  parseValue?: (s: string) => number | null;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.select(); }, [editing]);

  const displayed = formatValue ? formatValue(value) : String(value);
  const commit = () => {
    if (parseValue && draft.trim()) {
      const idx = parseValue(draft.trim());
      if (idx !== null && Number.isFinite(idx)) onChange(Math.max(min, Math.min(max, Math.round(idx))));
    }
    setEditing(false);
  };

  return (
    <div className="ze-control-row">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div className="ze-control-label">{label}</div>
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
            onBlur={commit}
            style={{ width: 90, textAlign: 'center', border: 'none', borderBottom: '1px solid var(--slate)', fontSize: '0.85rem', background: 'transparent', outline: 'none', fontFamily: 'var(--card-mono)' }}
          />
        ) : (
          <span
            onClick={parseValue ? () => { setDraft(displayed); setEditing(true); } : undefined}
            style={{ fontFamily: 'var(--card-mono)', fontSize: '0.85rem', color: 'var(--teal)', cursor: parseValue ? 'text' : 'default' }}
          >
            {displayed}
          </span>
        )}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ width: '100%', accentColor: 'var(--teal)' }}
      />
    </div>
  );
}
